import './itemsHorizontalScroll.css';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { isMobileDevice } from '../../../utils/device-utils';
import { useScreenSize } from '../../../hooks';

interface Props {
    children: React.ReactNode;
    className?: string;
    activateShadow?: boolean;
}

const ItemsHorizontalScroll = ({ children, className, activateShadow }: Props) => {
    const [atStart, setAtStart] = useState(true);
    const [atEnd, setAtEnd] = useState(false);
    const [scrollSmaller, setScrollSmaller] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const { width } = useScreenSize();
    const initialPadding = 0;

    const handleScroll = () => {
        if (!scrollContainerRef.current) return;
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
        setAtStart(scrollLeft <= 60);
        setAtEnd(scrollLeft + 60 + clientWidth >= scrollWidth);
    };

    const handleResize = () => {
        setScrollSmaller(scrollContainerRef.current!.scrollWidth <= scrollContainerRef.current!.clientWidth - 120);
        handleScroll(); // Update shadows when width changes
    };

    const hasInitializedRef = useRef(false);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) {
            return;
        }

        scrollContainer.addEventListener('scroll', handleScroll);

        const resizeObserver = new ResizeObserver(handleResize);
        resizeObserver.observe(scrollContainer);

        return () => {
            scrollContainer.removeEventListener('scroll', handleScroll);
            resizeObserver.unobserve(scrollContainer);
        };
    }, []);

    useEffect(() => {
        if (!scrollContainerRef.current || hasInitializedRef.current) {
            return;
        }

        const timer = setTimeout(() => {
            if (scrollContainerRef.current && !hasInitializedRef.current) {
                if (scrollContainerRef.current.scrollLeft <= 60) {
                    scrollContainerRef.current.scrollLeft = 60;
                }
                hasInitializedRef.current = true;
                handleScroll();
            }
        }, 50);

        return () => {
            clearTimeout(timer);
        };
    }, [children]);

    return (

        <div className={classNames("items-horizontal-scroll", className)} style={{
            position: 'relative',
            width: '100%',
        }}>
            <div hidden={!activateShadow && !!isMobileDevice()} className="items-horizontal-scroll__right-shadow" style={{
                opacity: atEnd ? 0 : 1,
                background: `linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, var(--bg-body) 100%)`
            }}>
                <button hidden={atEnd} onClick={(e) => {
                    if (!scrollContainerRef.current) return;
                    e.stopPropagation();
                    e.preventDefault();
                    scrollContainerRef.current.scrollBy({
                        left: scrollContainerRef.current.clientWidth - 250,
                        behavior: 'smooth'
                    });
                }}>
                    <i className="fa-solid fa-chevron-right"></i>
                </button>
            </div>
            <div hidden={!activateShadow && !!isMobileDevice()} className="items-horizontal-scroll__left-shadow" style={{
                opacity: atStart ? 0 : 1,
                background: `linear-gradient(-90deg, rgba(255, 255, 255, 0) 0%, var(--bg-body) 100%)`
            }}>
                <button hidden={atStart} onClick={(e) => {
                    if (!scrollContainerRef.current) return;
                    e.stopPropagation();
                    e.preventDefault();
                    scrollContainerRef.current.scrollBy({
                        left: -(scrollContainerRef.current.clientWidth - 250),
                        behavior: 'smooth'
                    });
                }}>
                    <i className="fa-solid fa-chevron-left"></i>
                </button>
            </div>
            <div ref={scrollContainerRef} className='horizontal-scroll__scroll-container' style={{
                display: 'flex',
                flexDirection: 'row',
                overflowX: 'scroll',
                overflowY: 'hidden',
                scrollbarWidth: 'none', /* Firefox */
                msOverflowStyle: 'none',  /* Internet Explorer 10+ */
            }}>
                <div style={{
                    width: ((atStart && atEnd) || scrollSmaller) ? initialPadding : 60 + initialPadding,
                    flexShrink: 0
                }} />
                {children}
                <div style={{
                    width: ((atStart && atEnd) || scrollSmaller) ? initialPadding : 60 + initialPadding,
                    flexShrink: 0
                }} />
            </div>
        </div>
    )
}

export default ItemsHorizontalScroll;
